
import React from 'react';
import { Referral } from '@/utils/referral/types';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, X, Trash, MessageSquare } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ReferralUsersTableProps {
  users: Referral[];
  onApprove: (userId: string) => void;
  onReject: (userId: string) => void;
  onDelete: (userId: string) => void;
}

const ReferralUsersTable: React.FC<ReferralUsersTableProps> = ({ users, onApprove, onReject, onDelete }) => {
  const { toast } = useToast();
  const [processingUsers, setProcessingUsers] = React.useState<string[]>([]);

  const handleApprove = async (userId: string) => {
    setProcessingUsers([...processingUsers, userId]);
    try {
      await onApprove(userId);
      toast({
        title: "Referral Approved",
        description: "Referral has been approved successfully."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve referral. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingUsers(processingUsers.filter(id => id !== userId));
    }
  };

  const handleReject = async (userId: string) => {
    setProcessingUsers([...processingUsers, userId]);
    try {
      await onReject(userId);
      toast({
        title: "Referral Rejected",
        description: "Referral has been rejected successfully."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject referral. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingUsers(processingUsers.filter(id => id !== userId));
    }
  };

  const handleDelete = async (userId: string) => {
    setProcessingUsers([...processingUsers, userId]);
    try {
      await onDelete(userId);
      toast({
        title: "Referral Deleted",
        description: "Referral has been deleted successfully."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete referral. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingUsers(processingUsers.filter(id => id !== userId));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'removed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (users.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-gray-500">No referral users found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Referral Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Badge className={getStatusColor(user.status)}>
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </Badge>
              </TableCell>
              <TableCell>
                {new Date(user.date).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <div className="flex justify-end space-x-2">
                  {user.status === 'pending' && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="bg-green-50 text-green-600 hover:bg-green-100 border-green-200"
                        onClick={() => handleApprove(user.id)}
                        disabled={processingUsers.includes(user.id)}
                      >
                        <Check className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="bg-red-50 text-red-600 hover:bg-red-100 border-red-200"
                        onClick={() => handleReject(user.id)}
                        disabled={processingUsers.includes(user.id)}
                      >
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                  {user.status !== 'pending' && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-red-600 hover:text-red-800"
                      onClick={() => handleDelete(user.id)}
                      disabled={processingUsers.includes(user.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ReferralUsersTable;
